// payment-receipts.component.ts
import { Component, OnInit } from '@angular/core';
import { PolicyService } from '../../shared/services/policy.service';
import { InvoiceService } from '../../shared/services/invoice.service';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { Policy, PaginatedPolicyResponse } from '../../models/policy.interface';
import { LoaderComponent } from '../../components/loader/loader.component';
import { CompanyService } from '../../shared/services/company.service';
import { HttpClient } from '@angular/common/http';
import html2pdf from 'html2pdf.js';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../shared/services/auth.service';

@Component({
  selector: 'app-payment-receipts',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    TranslateModule,
    LoaderComponent,
  ],
  templateUrl: './payment-receipts.component.html',
  styleUrls: ['./payment-receipts.component.scss'],
})
export class PaymentReceiptsComponent implements OnInit {
  policies: Policy[] = [];
  loading = true;
  error: string | null = null;
  currentSelectedCompanyId: number = 0;
  isDownloading = false;

  buyerMobileNumber: string = '';
  buyerEmail: string = '';
  buyerName: string = '';

  constructor(
    private policyService: PolicyService,
    private invoiceService: InvoiceService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private companyService: CompanyService,
    private authService: AuthService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.companyService.currentSelectedCompany$.subscribe((companyId) => {
      this.currentSelectedCompanyId = companyId;
      this.loadPolicies();
    });

    this.authService.userProfile$.subscribe((userProfile) => {
      const attributes = userProfile?.attributes as Record<
        string,
        string[] | undefined
      >;
      this.buyerName = `${userProfile?.firstName ?? ''} ${
        userProfile?.lastName ?? ''
      }`.trim();
      this.buyerEmail = userProfile?.email ?? '';
      this.buyerMobileNumber = attributes?.['PhoneNumber']?.[0] ?? '';
    });
  }

  private getUserIdFromLocalStorage(): string {
    const keycloakProfile = localStorage.getItem('keycloakUserProfile');
    if (keycloakProfile) {
      try {
        const profile = JSON.parse(keycloakProfile);
        return profile.id;
      } catch (error) {
        console.error('Error parsing Keycloak profile:', error);
        return '';
      }
    }
    return '';
  }

  loadPolicies(): void {
    this.loading = true;
    this.error = null;

    const userId = this.getUserIdFromLocalStorage();
    if (!userId) {
      this.error = 'User not authenticated. Please login again.';
      this.loading = false;
      return;
    }

    this.invoiceService
      .getInvoices(userId, this.currentSelectedCompanyId)
      .subscribe({
        next: (response: PaginatedPolicyResponse) => {
          this.policies = this.enrichWithInvoiceData(response.content);
          this.loading = false;
        },
        error: (err) => {
          this.error = this.translateService.instant(
            'ERRORS.FAILED_LOAD_POLICIES'
          );
          this.loading = false;
          console.error(err);
        },
      });
  }

  private enrichWithInvoiceData(policies: Policy[]): Policy[] {
    return policies.map((policy) => ({
      ...policy,
      invoice: {
        number: `INV-${policy.policyNumber}`,
        date: new Date(policy.startDate),
        downloadUrl: `/api/invoices/${policy.id}/pdf`,
        status: this.getInvoiceStatus(policy),
      },
    }));
  }

  private getInvoiceStatus(policy: Policy): 'paid' | 'pending' | 'overdue' {
    const now = new Date();
    const dueDate = new Date(policy.startDate);
    dueDate.setMonth(dueDate.getMonth() + 1); // Assume due 1 month after start

    if (policy.status === 'cancelled') return 'pending';
    if (now > dueDate) return 'overdue';
    return 'paid';
  }

  downloadPayerInvoicePdf(policyNumber: string): void {
    this.isDownloading = true;
    this.policyService.downloadInvoice(policyNumber).subscribe({
      next: (blob: Blob) => {
        saveAs(blob, `invoice_${policyNumber}.pdf`);
        this.snackBar.open('Invoice downloaded successfully', 'Close', {
          duration: 3000,
        });
        this.isDownloading = false;
      },
      error: (err) => {
        console.error('Failed to download payer invoice', err);
        this.snackBar.open('Failed to download payer invoice', 'Close', {
          duration: 3000,
          panelClass: ['error-snackbar'],
        });
        this.isDownloading = false;
      },
    });
  }

  getProviderInitials(providerName: string): string {
    const words = providerName.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return providerName.substring(0, 2).toUpperCase();
  }

  getProviderColor(providerName: string): string {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    let hash = 0;
    for (let i = 0; i < providerName.length; i++) {
      hash = providerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash % colors.length);
    return colors[colorIndex];
  }

  private convertImageToBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = url;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx!.drawImage(img, 0, 0);

        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = () => reject('Logo image not found');
    });
  }

  private formatDateOnly(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  }

  private safeValue(value: any): string {
    return value === null || value === undefined || value === '' ? '-' : value;
  }

  async downloadInvoicePdf(
    policyNumber: string
  ): Promise<void> {
    this.isDownloading = true;
    try {
      // Fetch invoice
      const invoice = await firstValueFrom(
        this.invoiceService.getInvoiceDetails(policyNumber)
      );

      // Load HTML template
      const template = await firstValueFrom(
        this.http.get('assets/templates/invoice-template.html', {
          responseType: 'text',
        })
      );

      // Convert logo to Base64 (from frontend assets)
      const logoBase64 = await this.convertImageToBase64(
        'assets/templates/waseel-logo.png'
      );

      let totalExclVat = 0;
      let totalVat = 0;

      invoice.items.forEach((item: any) => {
        const taxable = (item.amount ?? 0) - (item.taxAmount ?? 0);
        totalExclVat += taxable;
        totalVat += item.taxAmount ?? 0;
      });

      // Build items table
      let itemsRows = '';
      invoice.items.forEach((item: any, i: number) => {
        itemsRows += `
          <tr>
            <td>${i + 1}</td>
            <td>${item.description}</td>
            <td>${item.unitPrice}</td>
            <td>${item.quantity}</td>
            <td>${item.amount - item.taxAmount}</td>
            <td>${item.taxRate * 100}%</td>
            <td>${item.taxAmount}</td>
            <td>${item.amount}</td>
          </tr>`;
      });

      // Replace placeholders
      let html = template
        .replace(/{{logo}}/g, logoBase64)
        .replace(/{{invoiceNumber}}/g, invoice.invoiceNumber)
        .replace(/{{invoiceDate}}/g, invoice.invoiceDate)
        .replace(/{{customerName}}/g, this.safeValue(this.buyerName))
        .replace(/{{customerMobileNumber}}/g, this.safeValue(this.buyerMobileNumber))
        .replace(
          /{{customerEmail}}/g,
          this.safeValue(this.buyerEmail)
        )
        .replace(/{{customerTaxId}}/g, invoice.customerTaxId)
        .replace(
          /{{policyIssueDate}}/g,
          this.formatDateOnly(invoice.policyIssueDate)
        )
        .replace(
          /{{policyStartDate}}/g,
          this.formatDateOnly(invoice.policyStartDate)
        )
        .replace(
          /{{policyEndDate}}/g,
          this.formatDateOnly(invoice.policyEndDate)
        )
        .replace(/{{totalAmount}}/g, invoice.totalAmount)
        .replace(/{{itemsRows}}/g, itemsRows)
        .replace(/{{totalExclVat}}/g, totalExclVat.toFixed(2))
        .replace(/{{totalVat}}/g, totalVat.toFixed(2));

      // Insert into hidden container
      const element = document.getElementById('invoicePdf')!;
      element.innerHTML = html;
      element.style.display = 'block';

      // Generate PDF
      await html2pdf()
        .from(element)
        .set({
          margin: 10,
          filename: 'invoice.pdf',
          image: { type: 'jpeg', quality: 1 },
          html2canvas: { scale: 3, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .save();

      // Cleanup
      element.style.display = 'none';
      element.innerHTML = '';
      this.isDownloading = false;
    } catch (err) {
      console.error('PDF generation failed:', err);
      this.snackBar.open('Failed to download invoice PDF', 'Close', {
        duration: 3000,
      });
      this.isDownloading = false;
    }
  }
}
